<template>
  <div>
    <b-card>
      <b-card-header class="animated fadeInRightBig">
        <b-card-title>Importação de Usuários</b-card-title>
        <div class="d-flex align-items-center">
          <b-button
            variant="outline-primary"
            size="sm"
            class="mr-1"
            @click="downloadTemplate"
          >
            <feather-icon
              icon="DownloadIcon"
              class="mr-50"
            />
            Baixar Template
          </b-button>
        </div>
      </b-card-header>

      <b-card-body>
        <div class="mb-2">
          <h4>Instruções de Importação</h4>
          <ul>
            <li>O arquivo deve estar no formato Excel (.xlsx, .xls) ou CSV (.csv)</li>
            <li>A primeira linha deve conter os cabeçalhos: Nome da Empresa, Nome do Funcionário, Admissão, Departamento, Função, CPF, Email, STATUS, Alocação</li>
            <li>A data de admissão deve estar no formato DD/MM/AAAA ou como data do Excel</li>
            <li>O CPF pode conter ou não formatação (xxx.xxx.xxx-xx)</li>
            <li>Se a empresa, departamento, função ou alocação não existirem, serão criados automaticamente</li>
            <li><strong>Alocação é opcional</strong> - Se informada, o usuário será atrelado a esta alocação</li>
            <li>Defina a senha padrão que novos usuários receberão (existentes mantêm senha atual)</li>
            <li><strong>Usuários existentes (CPF ou Email) serão atualizados com TODOS os novos dados</strong></li>
            <li><strong>Você pode atualizar: Nome, Email, CPF, Data de Admissão, Departamento, Função, Empresa e Alocação</strong></li>
          </ul>
        </div>

        <b-card class="mb-2">
          <b-row>
            <b-col md="6">
              <label for="defaultPassword">Senha Padrão para Usuários Importados</label>
              <b-form-input
                id="defaultPassword"
                v-model="defaultPassword"
                type="text"
                placeholder="Ex: Mudar@123"
              />
              <small class="text-muted">
                Todos os usuários importados receberão esta senha. Mínimo 6 caracteres.
              </small>
            </b-col>
          </b-row>
        </b-card>

        <b-card class="border-primary">
          <div class="d-flex flex-column align-items-center justify-content-center py-2">
            <feather-icon
              icon="UploadCloudIcon"
              size="48"
              class="text-primary mb-1"
            />

            <h5 class="mb-1">
              Selecione o arquivo para importação
            </h5>
            <p class="text-muted mb-2">
              Arraste e solte ou clique para selecionar
            </p>

            <b-form-file
              ref="fileInput"
              v-model="file"
              :state="Boolean(file)"
              placeholder="Escolha um arquivo..."
              drop-placeholder="Solte o arquivo aqui..."
              accept=".xlsx,.xls,.csv"
              class="mb-1"
              @change="onFileChange"
            />

            <div
              v-if="file"
              class="mt-1"
            >
              <b-badge
                variant="light-success"
                class="d-flex align-items-center"
              >
                <feather-icon
                  icon="FileIcon"
                  size="16"
                  class="mr-50"
                />
                {{ file.name }} ({{ formatFileSize(file.size) }})
              </b-badge>
            </div>

            <div
              v-if="importing"
              class="w-100 mt-2"
            >
              <div class="d-flex justify-content-between mb-1">
                <small class="text-muted">
                  <b-spinner
                    small
                    class="mr-50"
                    variant="primary"
                  />
                  <strong>{{ progressText }}</strong>
                </small>
                <small
                  v-if="estimatedRows > 0"
                  class="text-muted"
                >
                  <strong>{{ progressPercent }}%</strong>
                </small>
              </div>
              <b-progress
                :value="progressPercent"
                height="1.5rem"
                animated
                striped
              >
                <b-progress-bar
                  :value="progressPercent"
                  variant="primary"
                >
                  <strong>{{ progressPercent }}%</strong>
                </b-progress-bar>
              </b-progress>
              <div class="mt-1">
                <small
                  v-if="currentUser"
                  class="text-muted"
                >
                  Processando: <strong>{{ currentUser }}</strong>
                </small>
                <small
                  v-else
                  class="text-muted"
                >{{ elapsedTime }}</small>
              </div>
            </div>

            <div class="mt-2">
              <b-button
                variant="outline-info"
                :disabled="!file || validating || importing"
                class="mr-1"
                @click="validateFile"
              >
                <b-spinner
                  v-if="validating"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="CheckCircleIcon"
                  class="mr-50"
                />
                {{ validating ? 'Validando...' : 'Verificar Arquivo' }}
              </b-button>

              <b-button
                variant="primary"
                :disabled="!file || importing"
                class="mr-1"
                @click="importUsers"
              >
                <b-spinner
                  v-if="importing"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="UploadIcon"
                  class="mr-50"
                />
                {{ importing ? 'Importando...' : 'Importar Usuários' }}
              </b-button>

              <b-button
                variant="outline-secondary"
                :disabled="!file || importing"
                @click="clearFile"
              >
                Limpar
              </b-button>
            </div>
          </div>
        </b-card>

        <b-card
          v-if="validationResult && !importResult"
          class="mt-2"
        >
          <b-card-title>Preview da Validação</b-card-title>

          <div class="row text-center mb-2">
            <div class="col">
              <h3 class="text-primary mb-0">
                {{ validationResult.total }}
              </h3>
              <small class="text-muted">Total de Linhas</small>
            </div>
            <div class="col">
              <h3 class="text-success mb-0">
                {{ validationResult.valid }}
              </h3>
              <small class="text-muted">Válidas</small>
            </div>
            <div class="col">
              <h3 class="text-danger mb-0">
                {{ validationResult.invalid }}
              </h3>
              <small class="text-muted">Inválidas</small>
            </div>
          </div>

          <b-alert
            v-if="validationResult.invalid > 0"
            variant="warning"
            show
            class="mb-2"
          >
            <strong>Atenção!</strong> O arquivo contém {{ validationResult.invalid }} linhas com erros.
            Corrija os erros antes de importar.
          </b-alert>

          <b-alert
            v-else
            variant="success"
            show
            class="mb-2"
          >
            <strong>Sucesso!</strong> Todas as linhas estão válidas. Você pode prosseguir com a importação.
          </b-alert>

          <h5 class="mb-1">
            Preview das primeiras 10 linhas
          </h5>
          <b-table
            :items="validationResult.preview"
            :fields="previewFields"
            responsive
            striped
            hover
            small
            class="mb-0"
          >
            <template #cell(valid)="data">
              <b-badge :variant="data.value ? 'success' : 'danger'">
                {{ data.value ? 'Válida' : 'Inválida' }}
              </b-badge>
            </template>

            <template #cell(data.name)="data">
              {{ data.item.data.name }}
            </template>

            <template #cell(data.email)="data">
              {{ data.item.data.email }}
            </template>

            <template #cell(error.message)="data">
              <span
                v-if="data.item.error"
                class="text-danger"
              >
                {{ data.item.error.message }}
              </span>
            </template>
          </b-table>

          <div class="mt-2 text-center">
            <b-button
              v-if="validationResult.invalid === 0"
              variant="primary"
              @click="importUsers"
            >
              <feather-icon
                icon="UploadIcon"
                class="mr-50"
              />
              Prosseguir com Importação
            </b-button>

            <b-button
              variant="outline-secondary"
              @click="validationResult = null"
            >
              Fechar Preview
            </b-button>
          </div>
        </b-card>

        <b-card
          v-if="importResult"
          class="mt-2"
        >
          <b-card-title>Resultado da Importação</b-card-title>

          <div class="row text-center mb-2">
            <div class="col">
              <h3 class="text-primary mb-0">
                {{ importResult.total }}
              </h3>
              <small class="text-muted">Total</small>
            </div>
            <div class="col">
              <h3 class="text-info mb-0">
                {{ importResult.created || 0 }}
              </h3>
              <small class="text-muted">Criados</small>
            </div>
            <div class="col">
              <h3 class="text-warning mb-0">
                {{ importResult.updated || 0 }}
              </h3>
              <small class="text-muted">Atualizados</small>
            </div>
            <div class="col">
              <h3 class="text-secondary mb-0">
                {{ importResult.skipped || 0 }}
              </h3>
              <small class="text-muted">Sem Alteração</small>
            </div>
            <div class="col">
              <h3 class="text-danger mb-0">
                {{ importResult.failed }}
              </h3>
              <small class="text-muted">Falhas</small>
            </div>
          </div>

          <div
            v-if="importResult.success.length > 0"
            class="mt-2"
          >
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h5 class="text-success mb-0">
                <feather-icon
                  icon="CheckCircleIcon"
                  class="mr-50"
                />
                Usuários Importados com Sucesso
              </h5>
            </div>

            <b-row class="mb-1">
              <b-col md="4">
                <b-form-input
                  v-model="successSearchName"
                  placeholder="Pesquisar por nome..."
                  size="sm"
                />
              </b-col>
              <b-col md="4">
                <b-form-input
                  v-model="successSearchEmail"
                  placeholder="Pesquisar por email..."
                  size="sm"
                />
              </b-col>
              <b-col md="4">
                <b-form-select
                  v-model="successFilterAction"
                  :options="actionFilterOptions"
                  size="sm"
                />
              </b-col>
            </b-row>

            <b-table
              :items="successItems"
              :fields="successFields"
              :busy="loadingSuccess"
              responsive
              striped
              hover
              small
              class="mb-0"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-1" />
                  <strong>Carregando...</strong>
                </div>
              </template>
              <template #cell(line)="data">
                Linha {{ data.value }}
              </template>
              <template #cell(action)="data">
                <b-badge
                  :variant="
                    data.value === 'Criado' ? 'light-success' :
                    data.value === 'Atualizado' ? 'light-warning' :
                    'light-secondary'
                  "
                >
                  {{ data.value }}
                </b-badge>
              </template>
              <template #cell(changes)="data">
                <small class="text-muted">{{ data.value || '-' }}</small>
              </template>
            </b-table>

            <b-pagination
              v-if="successTotalRows > successPerPage"
              v-model="successCurrentPage"
              :total-rows="successTotalRows"
              :per-page="successPerPage"
              align="center"
              size="sm"
              class="mt-1 mb-0"
            />
          </div>

          <div
            v-if="importResult.errors.length > 0"
            class="mt-2"
          >
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h5 class="text-danger mb-0">
                <feather-icon
                  icon="AlertCircleIcon"
                  class="mr-50"
                />
                Erros Encontrados
              </h5>
            </div>

            <b-row class="mb-1">
              <b-col md="12">
                <b-form-input
                  v-model="errorSearchMessage"
                  placeholder="Pesquisar por mensagem de erro..."
                  size="sm"
                />
              </b-col>
            </b-row>

            <b-table
              :items="errorItems"
              :fields="errorFields"
              :busy="loadingErrors"
              responsive
              striped
              hover
              small
              class="mb-0"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-1" />
                  <strong>Carregando...</strong>
                </div>
              </template>
              <template #cell(line)="data">
                Linha {{ data.value }}
              </template>
            </b-table>

            <b-pagination
              v-if="errorTotalRows > errorPerPage"
              v-model="errorCurrentPage"
              :total-rows="errorTotalRows"
              :per-page="errorPerPage"
              align="center"
              size="sm"
              class="mt-1 mb-0"
            />
          </div>

          <div class="mt-2 text-center">
            <b-button
              variant="outline-primary"
              @click="resetImport"
            >
              <feather-icon
                icon="RefreshCwIcon"
                class="mr-50"
              />
              Nova Importação
            </b-button>
          </div>
        </b-card>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BButton,
  BAlert,
  BFormFile,
  BFormInput,
  BFormSelect,
  BBadge,
  BSpinner,
  BTable,
  BRow,
  BCol,
  BProgress,
  BProgressBar,
  BPagination,
} from 'bootstrap-vue'

export default {
  name: 'UserImport',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BButton,
    BAlert,
    BFormFile,
    BFormInput,
    BFormSelect,
    BBadge,
    BSpinner,
    BTable,
    BRow,
    BCol,
    BProgress,
    BProgressBar,
    BPagination,
  },
  data() {
    return {
      file: null,
      importing: false,
      validating: false,
      validationResult: null,
      importResult: null,
      defaultPassword: 'Mudar@123',
      estimatedRows: 0,
      estimatedTime: '0s',
      progressPercent: 0,
      progressText: 'Iniciando...',
      elapsedTime: 'Tempo decorrido: 0s',
      startTime: null,
      progressInterval: null,
      pollingInterval: null,
      jobId: null,
      currentUser: null,
      successSearchName: '',
      successSearchEmail: '',
      successFilterAction: 'all',
      successCurrentPage: 1,
      successPerPage: 50,
      successTotalRows: 0,
      successItems: [],
      errorSearchMessage: '',
      errorCurrentPage: 1,
      errorPerPage: 50,
      errorTotalRows: 0,
      errorItems: [],
      loadingSuccess: false,
      loadingErrors: false,
      actionFilterOptions: [
        { value: 'all', text: 'Todas as Ações' },
        { value: 'Criado', text: 'Criados' },
        { value: 'Atualizado', text: 'Atualizados' },
        { value: 'Sem Alteração', text: 'Sem Alteração' },
      ],
      successFields: [
        { key: 'line', label: 'Linha', thStyle: 'width: 80px' },
        { key: 'name', label: 'Nome' },
        { key: 'email', label: 'Email' },
        { key: 'action', label: 'Ação', thStyle: 'width: 130px' },
        { key: 'changes', label: 'Campos Alterados' },
      ],
      errorFields: [
        { key: 'line', label: 'Linha' },
        { key: 'message', label: 'Erro' },
      ],
      previewFields: [
        { key: 'line', label: 'Linha', thStyle: 'width: 80px' },
        { key: 'valid', label: 'Status', thStyle: 'width: 100px' },
        { key: 'data.name', label: 'Nome' },
        { key: 'data.email', label: 'Email' },
        { key: 'error.message', label: 'Erro' },
      ],
    }
  },
  computed: {
    useBackendFiltering() {
      if (!this.importResult) return false
      const totalSuccess = this.importResult.success?.length || 0
      const totalErrors = this.importResult.errors?.length || 0
      return (totalSuccess + totalErrors) > 1000
    },
  },
  watch: {
    successCurrentPage() {
      if (this.useBackendFiltering) {
        this.loadSuccessResults()
      } else {
        this.filterSuccessResults()
      }
    },
    successSearchName() {
      this.successCurrentPage = 1
      if (this.useBackendFiltering) {
        this.loadSuccessResults()
      } else {
        this.filterSuccessResults()
      }
    },
    successSearchEmail() {
      this.successCurrentPage = 1
      if (this.useBackendFiltering) {
        this.loadSuccessResults()
      } else {
        this.filterSuccessResults()
      }
    },
    successFilterAction() {
      this.successCurrentPage = 1
      if (this.useBackendFiltering) {
        this.loadSuccessResults()
      } else {
        this.filterSuccessResults()
      }
    },
    errorCurrentPage() {
      if (this.useBackendFiltering) {
        this.loadErrorResults()
      } else {
        this.filterErrorResults()
      }
    },
    errorSearchMessage() {
      this.errorCurrentPage = 1
      if (this.useBackendFiltering) {
        this.loadErrorResults()
      } else {
        this.filterErrorResults()
      }
    },
  },
  methods: {
    onFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        this.file = file
      }
    },
    clearFile() {
      this.file = null
      this.validationResult = null
      this.importResult = null
      this.$refs.fileInput.reset()
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`
    },

    async downloadTemplate() {
      try {
        const response = await this.$http.get(`${this.$store.state.api}users/import/template`, {
          responseType: 'blob',
        })

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })

        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', 'template_importacao_usuarios.xlsx')
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        setTimeout(() => URL.revokeObjectURL(url), 100)

        this.$message('Sucesso', 'Template baixado com sucesso', 'success')
      } catch (error) {
        this.$message('Erro', error.response?.data?.message || 'Erro ao baixar template', 'error')
      }
    },
    async validateFileInternal() {
      try {
        const formData = new FormData()
        formData.append('file', this.file)

        const response = await this.$http.post(
          `${this.$store.state.api}users/import/validate`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        )

        this.validationResult = response.data.data
      } catch (error) {
      }
    },
    async validateFile() {
      if (!this.file) {
        this.$message('Atenção', 'Selecione um arquivo', 'warning')
        return
      }

      this.validating = true
      this.validationResult = null
      this.importResult = null

      try {
        const formData = new FormData()
        formData.append('file', this.file)

        const response = await this.$http.post(
          `${this.$store.state.api}users/import/validate`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        )

        this.validationResult = response.data.data

        const variant = this.validationResult.invalid > 0 ? 'warning' : 'success'
        const title = this.validationResult.invalid > 0 ? 'Atenção' : 'Sucesso'
        this.$message(
          title,
          `${this.validationResult.total} linhas encontradas (${this.validationResult.valid} válidas, ${this.validationResult.invalid} inválidas)`,
          variant,
        )
      } catch (error) {
        this.$message('Erro', error.response?.data?.message || 'Erro ao validar arquivo', 'error')
      } finally {
        this.validating = false
      }
    },
    async importUsers() {
      if (!this.file) {
        this.$message('Atenção', 'Selecione um arquivo', 'warning')
        return
      }

      if (!this.defaultPassword || this.defaultPassword.length < 6) {
        this.$message('Atenção', 'A senha deve ter no mínimo 6 caracteres', 'warning')
        return
      }

      this.importing = true
      this.importResult = null
      this.progressPercent = 0
      this.progressText = 'Iniciando importação...'
      this.currentUser = null
      this.startTime = Date.now()

      try {
        if (!this.validationResult) {
          await this.validateFileInternal()
        }

        this.estimatedRows = this.validationResult?.total || 10

        const formData = new FormData()
        formData.append('file', this.file)
        formData.append('default_password', this.defaultPassword)

        const response = await this.$http.post(`${this.$store.state.api}users/import/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        this.jobId = response.data.job_id

        this.startPolling()
      } catch (error) {
        this.importing = false
        this.$message('Erro', error.response?.data?.message || error.response?.data?.error || 'Erro ao iniciar importação', 'error')
      }
    },
    startPolling() {
      this.pollingInterval = setInterval(async () => {
        try {
          const response = await this.$http.get(
            `${this.$store.state.api}users/import/progress/${this.jobId}`,
          )

          const progress = response.data.data

          this.progressPercent = Math.round(progress.percent) || 0
          this.progressText = `${progress.current}/${progress.total}`
          this.currentUser = progress.message.replace('Processando: ', '')

          const elapsed = Date.now() - this.startTime
          const elapsedSeconds = Math.floor(elapsed / 1000)
          if (elapsedSeconds < 60) {
            this.elapsedTime = `Tempo decorrido: ${elapsedSeconds}s`
          } else {
            const minutes = Math.floor(elapsedSeconds / 60)
            const seconds = elapsedSeconds % 60
            this.elapsedTime = `Tempo decorrido: ${minutes}m ${seconds}s`
          }

          if (progress.status === 'completed') {
            this.stopPolling()
            this.importResult = progress.results

            const result = progress.results
            const parts = []
            if (result.created) parts.push(`${result.created} criados`)
            if (result.updated) parts.push(`${result.updated} atualizados`)
            if (result.skipped) parts.push(`${result.skipped} sem alteração`)
            const message = parts.join(', ')

            this.$message('Sucesso', message || 'Nenhuma alteração necessária', 'success')

            this.$nextTick(() => {
              if (this.useBackendFiltering) {
                this.loadSuccessResults()
                this.loadErrorResults()
              } else {
                this.filterSuccessResults()
                this.filterErrorResults()
              }
            })
          } else if (progress.status === 'failed') {
            this.stopPolling()
            this.$message('Erro', progress.message, 'error')
          }
        } catch (error) {
        }
      }, 1000)
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
      this.importing = false
      this.jobId = null
    },
    async loadSuccessResults() {
      if (!this.jobId || !this.importResult) return

      this.loadingSuccess = true
      try {
        const params = {
          type: 'success',
          page: this.successCurrentPage,
          per_page: this.successPerPage,
          search_name: this.successSearchName,
          search_email: this.successSearchEmail,
          filter_action: this.successFilterAction,
        }

        const response = await this.$http.get(
          `${this.$store.state.api}users/import/results/${this.jobId}`,
          { params },
        )

        this.successItems = response.data.data
        this.successTotalRows = response.data.meta.total
      } catch (error) {
        if (error.response?.status === 404) {
          this.$message('Aviso', 'Resultados não encontrados ou expirados', 'warning')
        }
      } finally {
        this.loadingSuccess = false
      }
    },
    async loadErrorResults() {
      if (!this.jobId || !this.importResult) return

      this.loadingErrors = true
      try {
        const params = {
          type: 'errors',
          page: this.errorCurrentPage,
          per_page: this.errorPerPage,
          search_message: this.errorSearchMessage,
        }

        const response = await this.$http.get(
          `${this.$store.state.api}users/import/results/${this.jobId}`,
          { params },
        )

        this.errorItems = response.data.data
        this.errorTotalRows = response.data.meta.total
      } catch (error) {
        if (error.response?.status === 404) {
          this.$message('Aviso', 'Resultados não encontrados ou expirados', 'warning')
        }
      } finally {
        this.loadingErrors = false
      }
    },
    filterSuccessResults() {
      if (!this.importResult || !this.importResult.success) return

      let items = this.importResult.success

      if (this.successSearchName) {
        items = items.filter(item => item.name.toLowerCase().includes(this.successSearchName.toLowerCase()))
      }

      if (this.successSearchEmail) {
        items = items.filter(item => item.email.toLowerCase().includes(this.successSearchEmail.toLowerCase()))
      }

      if (this.successFilterAction !== 'all') {
        items = items.filter(item => item.action === this.successFilterAction)
      }

      this.successTotalRows = items.length
      const start = (this.successCurrentPage - 1) * this.successPerPage
      const end = start + this.successPerPage
      this.successItems = items.slice(start, end)
    },
    filterErrorResults() {
      if (!this.importResult || !this.importResult.errors) return

      let items = this.importResult.errors

      if (this.errorSearchMessage) {
        items = items.filter(item => item.message.toLowerCase().includes(this.errorSearchMessage.toLowerCase()))
      }

      this.errorTotalRows = items.length
      const start = (this.errorCurrentPage - 1) * this.errorPerPage
      const end = start + this.errorPerPage
      this.errorItems = items.slice(start, end)
    },
    resetImport() {
      this.importResult = null
      this.successSearchName = ''
      this.successSearchEmail = ''
      this.successFilterAction = 'all'
      this.successCurrentPage = 1
      this.successItems = []
      this.successTotalRows = 0
      this.errorSearchMessage = ''
      this.errorCurrentPage = 1
      this.errorItems = []
      this.errorTotalRows = 0
      this.stopPolling()
      this.clearFile()
    },
  },
}
</script>

<style scoped>
.required {
  color: red;
}

.alert-body ul {
  padding-left: 1.5rem;
}
</style>
